import { createSharedState } from 'preact-shared-state-hook'
import type { Doodle } from '../../../server/src/models'
import { DoodleOrder, sortDoodles } from '../../shared/doodleOrder'
import { comparing } from '../../shared/utils'

export const doodleGroupings = [
  ['', 'グループ化なし'],
  ['owner', '所有者ごと'],
  ['author', '作成者ごと'],
] as const

export type DoodleGrouping = typeof doodleGroupings[number][0]

const STORAGE_KEY_DOODLE_GROUPING = 'zumeco:folders:doodleGrouping'
const initialDoodleGrouping = localStorage.getItem(STORAGE_KEY_DOODLE_GROUPING)
const [useDoodleGrouping, _setDoodleGrouping] = createSharedState<DoodleGrouping>(
  initialDoodleGrouping && doodleGroupings.some(([grouping]) => grouping === initialDoodleGrouping)
    ? (initialDoodleGrouping as DoodleGrouping)
    : '',
)

export const setDoodleGrouping = (grouping: DoodleGrouping) => {
  grouping ? localStorage.setItem(STORAGE_KEY_DOODLE_GROUPING, grouping) : localStorage.removeItem(STORAGE_KEY_DOODLE_GROUPING)
  _setDoodleGrouping(grouping)
}

export const groupDoodles = (
  doodles: readonly Doodle[],
  grouping: DoodleGrouping,
  order: DoodleOrder,
  priorUid?: string,
): Record<string, readonly Doodle[]> => {
  const groupUserIdSelector = grouping === 'author' ? (doodle: Doodle) => doodle.authorUserId : (doodle: Doodle) => doodle.ownerUserId
  const groupUserIdDoodlesMap = new Map<string, Doodle[]>()
  for (const doodle of doodles) {
    const groupKey = groupUserIdSelector(doodle)
    const group = groupUserIdDoodlesMap.get(groupKey)
    group ? group.push(doodle) : groupUserIdDoodlesMap.set(groupKey, [doodle])
  }

  const groupUserNameDoodlesMap = new Map<string, Doodle[]>()
  const sortedDoodles = [...doodles].sort(comparing((x: Doodle) => -x.createdAt))
  for (const [uid, doodles] of groupUserIdDoodlesMap) {
    const user = sortedDoodles.find(doodle => doodle.users[uid])?.users[uid]
    groupUserNameDoodlesMap.set(user?.name || user?.email || uid, sortDoodles(doodles, order))
  }

  return Object.fromEntries(
    [...groupUserNameDoodlesMap].sort(
      comparing(
        ([, doodles]: [string, Doodle[]]) => groupUserIdSelector(doodles[0]) !== priorUid,
        ([userName]: [string, Doodle[]]) => userName,
      ),
    ),
  )
}

export { useDoodleGrouping }
